import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Avatar, Collapse, IconButton, Tooltip, Badge, useTheme, Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  Category as CategoryIcon,
  RequestQuote as RFQIcon,
  RequestQuote as QuotationIcon,
  Description as ContractIcon,
  MoveToInbox as GRIcon,
  CompareArrows as ReconIcon,
  Outbox as GIIcon,
  SwapHoriz as TransferIcon,
  FactCheck as PIIcon,
  AdminPanelSettings as SuperAdminIcon,
  ManageAccounts as AdminIcon,
  Warehouse as WarehouseIcon,
  Public as PortalIcon,
  HourglassTop as ApprovalIcon,
  HelpOutline as HelpIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Logout as LogoutIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as CreditNoteIcon,
  RequestPage as DebitNoteIcon,
  Payment as PaymentIcon,
  RocketLaunch as DeployIcon,
  GitHub as GitHubIcon,
  SmartToy as SmartToyIcon,
  Toll as TokenIcon,
  Tune as TuneIcon,
  LocalOffer as PricingIcon,
  Star as StarIcon,
  LockOutlined as LockIcon,
  Factory as FactoryIcon,
  Engineering as SESIcon,
  Handshake as ConsignmentIcon,
  Inventory2 as ErpIcon,
  Route as EwbIcon,
  AssignmentReturn as ReturnsIcon,
  PictureAsPdf as TemplateBuilderIcon,
} from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import { useThemeContext } from '../../contexts/ThemeContext';
import { getUserDisplayName, getUserBadge } from '../../utils/superAdminChecker';
import { useRole } from '../../contexts/RoleContext';
import apiClient from '../../api/apiClient';
import { useSubscription } from '../../contexts/SubscriptionContext';

export const DRAWER_WIDTH = 272;
export const DRAWER_WIDTH_COLLAPSED = 72;

const menuConfig = [
  {
    section: 'OVERVIEW',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', tag: 'Beta' },
    ],
  },
  {
    section: 'MASTER DATA',
    collapsible: true,
    items: [
      { text: 'Products',   icon: <CategoryIcon />,  path: '/products' },
      { text: 'Suppliers',  icon: <StoreIcon />,     path: '/suppliers',  featureKey: 'suppliers' },
      { text: 'Customers',  icon: <PeopleIcon />,    path: '/customers',  featureKey: 'customers' },
      { text: 'Plants',     icon: <WarehouseIcon />, path: '/plants',     featureKey: 'plants' },
    ],
  },
  {
    section: 'PROCUREMENT',
    collapsible: true,
    items: [
      { text: 'RFQ / Bids',      icon: <RFQIcon />,           path: '/rfqs',                  featureKey: 'procurement' },
      { text: 'Contracts',       icon: <ContractIcon />,      path: '/contracts',             featureKey: 'procurement' },
      { text: 'Purchase Orders', icon: <ShoppingCartIcon />,  path: '/purchase-orders',       featureKey: 'procurement' },
      { text: 'Debit Notes',     icon: <DebitNoteIcon />,     path: '/debit-notes',           featureKey: 'procurement' },
      { text: 'Service Entries', icon: <SESIcon />,           path: '/ses',                   featureKey: 'procurement' },
      { text: 'Consignment',     icon: <ConsignmentIcon />,   path: '/consignment',           featureKey: 'procurement' },
      { text: 'Supplier Portal', icon: <PortalIcon />,        path: '/supplier-portal-manage',featureKey: 'supplier_portal' },
    ],
  },
  {
    section: 'SALES',
    collapsible: true,
    items: [
      { text: 'Quotations',   icon: <QuotationIcon />,      path: '/quotations',   featureKey: 'sales' },
      { text: 'Sales Orders', icon: <LocalShippingIcon />,  path: '/sales-orders', featureKey: 'sales' },
      { text: 'Credit Notes', icon: <CreditNoteIcon />,     path: '/credit-notes', featureKey: 'sales' },
    ],
  },
  {
    section: 'WAREHOUSE',
    collapsible: true,
    items: [
      { text: 'Stock Overview',  icon: <InventoryIcon />, path: '/inventory',          featureKey: 'inventory' },
      { text: 'Goods Receipts',  icon: <GRIcon />,        path: '/goods-receipts',     featureKey: 'warehouse' },
      { text: 'Reconciliation',  icon: <ReconIcon />,     path: '/reconciliation',     featureKey: 'warehouse' },
      { text: 'Returns',        icon: <ReturnsIcon />,   path: '/returns',            featureKey: 'warehouse' },
      { text: 'Goods Issues',    icon: <GIIcon />,        path: '/goods-issues',       featureKey: 'warehouse' },
      { text: 'E-Way Bills',     icon: <EwbIcon />,       path: '/eway-bills',         featureKey: 'warehouse' },
      { text: 'Transfers',       icon: <TransferIcon />,  path: '/transfer-postings',  featureKey: 'warehouse' },
      { text: 'Physical Count',  icon: <PIIcon />,        path: '/physical-inventory', featureKey: 'warehouse' },
      { text: 'Production',      icon: <FactoryIcon />,   path: '/production',         featureKey: 'warehouse' },
    ],
  },
  {
    section: 'ANALYTICS',
    collapsible: true,
    items: [
      { text: 'Inventory Reports', icon: <AssessmentIcon />, path: '/reports/inventory',   featureKey: 'basic_reports' },
      { text: 'Sales Reports',     icon: <AssessmentIcon />, path: '/reports/sales',        featureKey: 'basic_reports' },
      { text: 'Procurement',       icon: <AssessmentIcon />, path: '/reports/procurement',  featureKey: 'basic_reports' },
      { text: 'Fast-Moving Items', icon: <AssessmentIcon />, path: '/reports/fast-moving',  featureKey: 'basic_reports' },
      { text: 'Category Analysis', icon: <AssessmentIcon />, path: '/reports/category-analysis', featureKey: 'basic_reports' },
    ],
  },
  {
    section: 'AI AGENTS',
    collapsible: true,
    items: [
      { text: 'Agent Inbox',  icon: <SmartToyIcon />,  path: '/agents/inbox',    badge: true, featureKey: 'ai_agents' },
      { text: 'Run History',  icon: <AnalyticsIcon />, path: '/agents/history',  featureKey: 'ai_agents' },
    ],
  },
  {
    section: 'APPROVALS',
    collapsible: true,
    items: [
      { text: 'Pending Approvals', icon: <ApprovalIcon />, path: '/approvals/pending', featureKey: 'approvals' },
    ],
  },
  {
    section: 'SUPPORT',
    items: [
      { text: 'Help & Support', icon: <HelpIcon />, path: '/help' },
    ],
  },
];

const adminMenuConfig = {
  section: 'ADMIN',
  collapsible: true,
  items: [
    { text: 'Admin Panel',   icon: <AdminIcon />,    path: '/admin',        roles: ['Admin'] },
    { text: 'Document Templates', icon: <TemplateBuilderIcon />, path: '/document-templates', roles: ['Admin'] },
    { text: 'Subscription',  icon: <PricingIcon />,  path: '/subscription', roles: ['Admin'] },
  ],
};

const superAdminMenuConfig = {
  section: 'SUPER ADMIN',
  collapsible: true,
  items: [
    { text: 'Dashboard',  icon: <AnalyticsIcon />,  path: '/super-admin' },
    { text: 'Companies',  icon: <BusinessIcon />,   path: '/super-admin/companies' },
    { text: 'Users',      icon: <PeopleIcon />,     path: '/super-admin/users' },
    { text: 'Theme',      icon: <PaletteIcon />,    path: '/super-admin/theme' },
    { text: 'Settings',   icon: <SuperAdminIcon />, path: '/super-admin/settings' },
    { text: 'Plans',         icon: <PricingIcon />,    path: '/super-admin/plans' },
    { text: 'Plan Features', icon: <StarIcon />,      path: '/super-admin/plan-features' },
    { text: 'Invoices',      icon: <ReceiptIcon />,   path: '/super-admin/invoices' },
    { text: 'Payments',   icon: <PaymentIcon />,    path: '/super-admin/payments' },
    { text: 'Deploy',     icon: <DeployIcon />,     path: '/super-admin/deploy' },
    { text: 'Git',        icon: <GitHubIcon />,     path: '/super-admin/git' },
    { text: 'AI Agents',  icon: <SmartToyIcon />,  path: '/super-admin/agents' },
    { text: 'AI Tokens',  icon: <TokenIcon />,     path: '/super-admin/ai-tokens' },
    { text: 'AI Config',  icon: <TuneIcon />,      path: '/super-admin/ai-config' },
  ],
};

// All collapsible sections start collapsed — the sidebar is an accordion,
// not a fully-expanded tree, so only one area of nav is visible at a time.
const allCollapsibleGroups = [...menuConfig.filter(g => g.collapsible), adminMenuConfig, superAdminMenuConfig];
const initialSectionCollapsed = () =>
  allCollapsibleGroups.reduce((acc, g) => ({ ...acc, [g.section]: true }), {});

const Sidebar = ({ mobileOpen, handleDrawerToggle, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSuperAdmin, logout } = useAuth();
  const { isDarkMode } = useThemeContext();
  const theme = useTheme();
  const { hasAccess, can } = useRole();
  const { hasFeature } = useSubscription();
  const displayName = getUserDisplayName(user);
  const userBadge = getUserBadge(user);
  const [sectionCollapsed, setSectionCollapsed] = useState(initialSectionCollapsed);
  const [agentPendingCount, setAgentPendingCount] = useState(0);

  // Light, quiet chrome (reference-app inspired) that still respects the app's
  // own dark-mode toggle — unlike the old design, which was permanently dark
  // navy regardless of the user's theme preference.
  const surface = isDarkMode ? '#1a1f2e' : '#ffffff';
  const surfaceAlt = isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const textPrimary = isDarkMode ? '#e4e8ef' : '#1e293b';
  const textSecondary = isDarkMode ? '#8b95a5' : '#64748b';
  const textMuted = isDarkMode ? 'rgba(139,149,165,0.6)' : '#94a3b8';
  const hoverBg = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9';
  const primaryMain = theme.palette.primary.main;
  const primarySoftBg = isDarkMode ? `${primaryMain}26` : `${primaryMain}0f`;
  const primarySoftBorder = `${primaryMain}33`;

  const fetchAgentPendingCount = useCallback(async () => {
    const companyId = user?.activeCompanyId || user?.companyId;
    if (!companyId || isSuperAdmin) return;
    try {
      const res = await apiClient.get(`/api/agents/suggestions/${companyId}`, {
        params: { status: 'pending_review', limit: 1 },
      });
      setAgentPendingCount(res.data?.counts?.pending_review || 0);
    } catch {
      // non-critical
    }
  }, [user, isSuperAdmin]);

  useEffect(() => {
    fetchAgentPendingCount();
    const interval = setInterval(fetchAgentPendingCount, 60000);
    return () => clearInterval(interval);
  }, [fetchAgentPendingCount]);

  const toggleSection = (section) => {
    if (isCollapsed) return;
    setSectionCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  const isSectionActive = (items) => items.some(item => isActive(item.path));

  // Auto-expand whichever section contains the current route, so navigating
  // straight to a page (via URL, refresh, etc.) doesn't hide its nav entry
  // behind a collapsed accordion. Other sections stay collapsed.
  useEffect(() => {
    const activeGroup = allCollapsibleGroups.find(g => isSectionActive(g.items));
    if (activeGroup) {
      setSectionCollapsed(prev => (prev[activeGroup.section] ? { ...prev, [activeGroup.section]: false } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNav = (path) => {
    navigate(path);
    if (mobileOpen) handleDrawerToggle();
  };

  const currentWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    const badgeCount = item.badge ? agentPendingCount : 0;
    const locked = !isSuperAdmin && item.featureKey && !hasFeature(item.featureKey);
    const tooltipTitle = locked
      ? `Requires upgrade (${item.featureKey})`
      : isCollapsed ? item.text : '';

    return (
      <Tooltip title={tooltipTitle} placement="right" arrow key={item.path}>
        <ListItemButton
          onClick={() => locked ? handleNav('/subscription') : handleNav(item.path)}
          sx={{
            mx: isCollapsed ? 0.75 : 1.5,
            mb: 0.25,
            borderRadius: 1.5,
            py: 0.75,
            px: isCollapsed ? 0 : 1.5,
            minHeight: 40,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            position: 'relative',
            color: locked ? textMuted : active ? primaryMain : textSecondary,
            background: active && !locked ? primarySoftBg : 'transparent',
            border: '1px solid',
            borderColor: active && !locked ? primarySoftBorder : 'transparent',
            '&:hover': {
              background: locked ? hoverBg : active ? primarySoftBg : hoverBg,
              color: locked ? textMuted : active ? primaryMain : textPrimary,
            },
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <ListItemIcon sx={{
            color: locked ? textMuted : active ? primaryMain : 'inherit',
            minWidth: isCollapsed ? 0 : 34,
            mr: isCollapsed ? 0 : undefined,
            justifyContent: 'center',
            '& .MuiSvgIcon-root': { fontSize: 20 },
            transition: 'color 150ms',
          }}>
            <Badge
              badgeContent={badgeCount}
              color="warning"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.625rem',
                  height: 16,
                  minWidth: 16,
                  padding: '0 3px',
                },
              }}
            >
              {item.icon}
            </Badge>
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.813rem',
                fontWeight: active && !locked ? 600 : 500,
                letterSpacing: '0.01em',
                noWrap: true,
              }}
            />
          )}
          {!isCollapsed && item.tag && !locked && (
            <Chip
              label={item.tag}
              size="small"
              sx={{
                height: 16, fontSize: '0.563rem', fontWeight: 700, letterSpacing: '0.04em',
                px: 0.25, ml: 0.5, flexShrink: 0,
                color: 'primary.main', bgcolor: `${primaryMain}14`,
                border: '1px solid', borderColor: `${primaryMain}40`,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          {!isCollapsed && locked && (
            <LockIcon sx={{ fontSize: 12, color: textMuted, ml: 0.5, flexShrink: 0 }} />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  const drawer = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: surface,
      transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 200ms',
    }}>
      {/* Current app + Collapse Toggle — brand/logo lives in the Header now.
          The ERP pill moved here from the header so it's visible right
          where the user's eyes land when they open the sidebar. */}
      <Box sx={{
        display: { xs: 'none', sm: 'flex' },
        flexDirection: isCollapsed ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        gap: isCollapsed ? 1 : 0,
        px: isCollapsed ? 0 : 2.5, pt: 1.5, pb: 1,
        borderBottom: '1px solid',
        borderColor,
      }}>
        {isCollapsed ? (
          <Tooltip title="ERP" placement="right" arrow>
            <Box sx={{
              width: 26, height: 26, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: primarySoftBg, border: '1px solid', borderColor: primarySoftBorder,
            }}>
              <ErpIcon sx={{ fontSize: 15, color: primaryMain }} />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            px: 1.25, py: 0.5, borderRadius: 2,
            bgcolor: primarySoftBg, border: '1px solid', borderColor: primarySoftBorder,
          }}>
            <ErpIcon sx={{ fontSize: 15, color: primaryMain }} />
            <Typography sx={{ fontSize: '0.688rem', fontWeight: 700, color: primaryMain }}>ERP</Typography>
          </Box>
        )}
        <IconButton
          onClick={onToggleCollapse}
          size="small"
          sx={{
            width: 30, height: 30,
            color: textMuted,
            bgcolor: surfaceAlt,
            border: '1px solid',
            borderColor,
            '&:hover': { bgcolor: hoverBg, color: primaryMain },
            transition: 'all 150ms',
          }}
        >
          {isCollapsed ? <ExpandIcon sx={{ fontSize: 16 }} /> : <CollapseIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>

      {/* User Section */}
      {!isCollapsed ? (
        <Box sx={{ px: 2, py: 1 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 1.25, borderRadius: 1.5,
            bgcolor: surfaceAlt,
            border: '1px solid',
            borderColor,
            cursor: 'pointer',
            transition: 'all 150ms',
            '&:hover': { bgcolor: hoverBg },
          }}
          onClick={() => handleNav('/profile')}
          >
            <Avatar sx={{
              width: 34, height: 34,
              bgcolor: userBadge?.color === 'error' ? theme.palette.error.main : primaryMain,
              fontSize: '0.875rem', fontWeight: 700,
            }}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: textPrimary, fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3 }} noWrap>
                {displayName}
              </Typography>
              {userBadge && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    bgcolor: userBadge.color === 'error' ? theme.palette.error.main : primaryMain,
                  }} />
                  <Typography sx={{
                    color: userBadge.color === 'error' ? theme.palette.error.main : primaryMain,
                    fontSize: '0.625rem', fontWeight: 500,
                  }}>
                    {userBadge.label}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Tooltip title={`${displayName} (${userBadge?.label || 'User'})`} placement="right" arrow>
            <Avatar
              onClick={() => handleNav('/profile')}
              sx={{
                width: 34, height: 34, cursor: 'pointer',
                bgcolor: primaryMain,
                fontSize: '0.875rem', fontWeight: 700,
                border: '2px solid',
                borderColor: surface,
                '&:hover': { opacity: 0.85 },
                transition: 'opacity 150ms',
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', borderRadius: 2 },
      }}>
        {menuConfig.map((group) => {
          if (isSuperAdmin) return null;
          const visibleItems = group.items.filter(item => hasAccess(item.path));
          if (visibleItems.length === 0) return null;
          const isOpen = !sectionCollapsed[group.section];
          const hasActive = isSectionActive(visibleItems);

          return (
            <Box key={group.section} sx={{ mb: 0.25 }}>
              {!isCollapsed ? (
                <Box
                  onClick={() => group.collapsible && toggleSection(group.section)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2.5, py: 0.6, cursor: group.collapsible ? 'pointer' : 'default', userSelect: 'none',
                    '&:hover .section-arrow': { color: textSecondary },
                  }}
                >
                  <Typography variant="overline" sx={{
                    color: hasActive ? primaryMain : textMuted,
                    fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em',
                  }}>
                    {group.section}
                  </Typography>
                  {group.collapsible && (
                    <Box className="section-arrow" sx={{ color: textMuted, transition: 'all 150ms' }}>
                      {isOpen ? <KeyboardArrowDown sx={{ fontSize: 15 }} /> : <KeyboardArrowRight sx={{ fontSize: 15 }} />}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ borderTop: '1px solid', borderColor, mx: 1.5, my: 0.75 }} />
              )}
              <Collapse in={isCollapsed || isOpen} timeout={200}>
                <List disablePadding>
                  {visibleItems.map(renderMenuItem)}
                </List>
              </Collapse>
            </Box>
          );
        })}

        {/* Admin Section */}
        {!isSuperAdmin && can('admin.users') && (() => {
          const filteredItems = adminMenuConfig.items.filter(item => {
            if (item.roles) return can('admin.users');
            return true;
          });
          if (filteredItems.length === 0) return null;
          const isOpen = !sectionCollapsed[adminMenuConfig.section];
          const hasActive = isSectionActive(filteredItems);

          return (
            <Box sx={{ mb: 0.25 }}>
              {!isCollapsed ? (
                <Box onClick={() => toggleSection(adminMenuConfig.section)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 0.6, cursor: 'pointer', userSelect: 'none' }}>
                  <Typography variant="overline" sx={{ color: hasActive ? primaryMain : textMuted, fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em' }}>
                    {adminMenuConfig.section}
                  </Typography>
                  <Box sx={{ color: textMuted }}>
                    {isOpen ? <KeyboardArrowDown sx={{ fontSize: 15 }} /> : <KeyboardArrowRight sx={{ fontSize: 15 }} />}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ borderTop: '1px solid', borderColor, mx: 1.5, my: 0.75 }} />
              )}
              <Collapse in={isCollapsed || isOpen} timeout={200}>
                <List disablePadding>{filteredItems.map(renderMenuItem)}</List>
              </Collapse>
            </Box>
          );
        })()}

        {/* Super Admin Section */}
        {isSuperAdmin && (() => {
          const isOpen = !sectionCollapsed[superAdminMenuConfig.section];
          const hasActive = isSectionActive(superAdminMenuConfig.items);
          return (
            <Box sx={{ mb: 0.25 }}>
              {!isCollapsed ? (
                <Box onClick={() => toggleSection(superAdminMenuConfig.section)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 0.6, cursor: 'pointer', userSelect: 'none' }}>
                  <Typography variant="overline" sx={{ color: hasActive ? primaryMain : textMuted, fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em' }}>
                    {superAdminMenuConfig.section}
                  </Typography>
                  <Box sx={{ color: textMuted }}>
                    {isOpen ? <KeyboardArrowDown sx={{ fontSize: 15 }} /> : <KeyboardArrowRight sx={{ fontSize: 15 }} />}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ borderTop: '1px solid', borderColor, mx: 1.5, my: 0.75 }} />
              )}
              <Collapse in={isCollapsed || isOpen} timeout={200}>
                <List disablePadding>{superAdminMenuConfig.items.map(renderMenuItem)}</List>
              </Collapse>
            </Box>
          );
        })()}
      </Box>

      {/* Footer */}
      <Box sx={{ p: isCollapsed ? 0.75 : 1.5, borderTop: '1px solid', borderColor }}>
        <Tooltip title={isCollapsed ? 'Sign Out' : ''} placement="right" arrow>
          <Box
            onClick={() => { logout(); navigate('/login'); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: isCollapsed ? 0 : 1.5, py: 0.75,
              borderRadius: 1.5,
              color: textMuted,
              cursor: 'pointer',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              transition: 'all 150ms',
              '&:hover': { bgcolor: `${theme.palette.error.main}14`, color: theme.palette.error.main },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            {!isCollapsed && <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Sign Out</Typography>}
          </Box>
        </Tooltip>
        {!isCollapsed && (
          <Tooltip title="New Control Tower Dashboard included in this release — currently in beta." placement="top" arrow>
            <Typography sx={{
              color: textMuted, fontSize: '0.625rem', opacity: 0.7,
              textAlign: 'center', mt: 0.5, letterSpacing: '0.05em', cursor: 'default',
            }}>
              v1.1.0-beta &middot; Powered by KoorAI
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: currentWidth,
    bgcolor: surface,
    borderRight: '1px solid',
    borderColor,
    boxShadow: isDarkMode ? 'none' : '1px 0 0 rgba(0,0,0,0.02)',
    transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1), background-color 200ms',
    overflowX: 'hidden',
  };

  // The permanent (desktop) drawer sits below the now full-width header
  // instead of running the full viewport height beside it — matches
  // Header.jsx's Toolbar minHeight at the `sm` breakpoint, the only
  // breakpoint this drawer variant is ever visible at.
  const permanentDrawerPaperSx = {
    ...drawerPaperSx,
    top: 64,
    height: 'calc(100% - 64px)',
  };

  return (
    <Box component="nav" sx={{
      width: { sm: currentWidth },
      flexShrink: { sm: 0 },
      transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { ...drawerPaperSx, width: DRAWER_WIDTH } }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': permanentDrawerPaperSx }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
